import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Camera, FileWarning } from 'lucide-react';
import { characters } from '../data/universeData';

// Clearance levels determine what's visible
// 0 = fully classified, 1 = partial, 2 = open file (no spoilers still)
const getClearance = (character) => {
  // Characters with photos always get at least partial clearance
  if (character.image) return 2;
  // CMD Inner Circle core get partial
  if (character.id <= 3) return 2;
  // Already classified in data
  if (character.status === 'CLASSIFIED') return 0;
  // Later era / minor characters
  if (['Waterfront'].includes(character.era)) return 0;
  // Origins characters
  if (character.era === 'Origins') return 0;
  // Everyone else
  return 1;
};

// Redact status - never show DECEASED, EXILED, etc
const getDisplayStatus = (character) => {
  const cl = getClearance(character);
  if (cl === 0) return { label: 'CLASSIFIED', className: 'bg-[#8B1A1A]/20 text-[#8B1A1A] border border-[#8B1A1A]/30' };
  if (character.status === 'DECEASED') return { label: 'FILE SEALED', className: 'bg-[#8B1A1A]/10 text-[#8B1A1A]/70 border border-[#8B1A1A]/20' };
  if (character.status === 'UNKNOWN') return { label: 'WHEREABOUTS UNKNOWN', className: 'bg-[#E8E0D0]/5 text-[#E8E0D0]/40 border border-[#E8E0D0]/10' };
  if (character.status === 'EXILED') return { label: 'OFF THE GRID', className: 'bg-[#C9A84C]/5 text-[#C9A84C]/50 border border-[#C9A84C]/15' };
  if (character.status === 'ACTIVE') return { label: 'FILE ACTIVE', className: 'bg-[#1A2535] text-[#4A7AB5]/70 border border-[#4A7AB5]/20' };
  return { label: 'PENDING', className: 'bg-[#1A2535] text-[#E8E0D0]/30 border border-[#E8E0D0]/10' };
};

// Redact description - strip spoiler details
const getDisplayDescription = (character) => {
  const cl = getClearance(character);
  if (cl === 0) return character.description.startsWith('[') ? character.description : '[FILE CLASSIFIED]';
  // For partial/open, use description but it's already written carefully
  return character.description;
};

const renderThreatLevel = (level) => {
  return Array(5).fill(null).map((_, i) => (
    <span key={i} className={i < level ? 'text-[#C9A84C]' : 'text-[#333]'}>&#9632;</span>
  ));
};

const CharactersSection = () => {
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [showAll, setShowAll] = useState(false);

  // Top 4 featured characters
  const FEATURED_IDS = [1, 2, 3, 4]; // Anton, Vincent, Diego, Goonie
  const featuredChars = characters.filter(c => FEATURED_IDS.includes(c.id));
  const restChars = characters.filter(c => !FEATURED_IDS.includes(c.id));
  const displayChars = showAll ? characters : featuredChars;

  return (
    <section id="players" data-testid="characters-section" className="py-24 px-6 bg-[#080C10]">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[#C9A84C] mb-4" data-testid="characters-title">
            THE PLAYERS
          </h2>
          <p className="font-['Outfit'] text-[#E8E0D0]/60 text-base tracking-wide italic mb-2">
            "Everyone has a file. Not everyone's file is open."
          </p>
          <p className="font-['Outfit'] text-[#E8E0D0]/40 text-sm">
            Some files are sealed. Some photos are the only thing on record.
          </p>
        </motion.div>

        {/* Characters Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {displayChars.map((character, index) => {
            const clearance = getClearance(character);
            const status = getDisplayStatus(character);
            const desc = getDisplayDescription(character);

            return (
              <motion.div
                key={character.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                onClick={() => setSelectedCharacter(character)}
                className="dossier-card rounded-lg cursor-pointer hover:border-[#C9A84C]/30 transition-all duration-300 group overflow-hidden"
                data-testid={`character-card-${character.id}`}
              >
                {/* Photo section — removed per creative direction */}
                <div className="relative h-28 bg-[#0a0c10] flex items-center justify-center border-b border-[#1A2535]/40">
                  <Camera size={16} className="text-[#E8E0D0]/10" />
                  <span className="font-['Outfit'] text-[#E8E0D0]/15 text-[9px] tracking-widest ml-2">NO PHOTO ON FILE</span>
                </div>

                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-['Outfit'] text-[#C9A84C]/60 text-xs tracking-wider">
                      {clearance === 0 ? '[REDACTED]' : character.faction || character.era}
                    </span>
                  </div>

                  <h3 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#C9A84C] mb-1 group-hover:text-[#d4b55d] transition-colors">
                    {character.name}
                  </h3>

                  {character.archetype && clearance > 0 && (
                    <p className="font-['Outfit'] text-[#E8E0D0]/40 text-xs mb-2 italic">
                      {character.archetype}
                    </p>
                  )}

                  <p className="font-['Outfit'] text-[#E8E0D0]/60 text-sm mb-3 line-clamp-2">
                    {desc}
                  </p>

                  <div className="flex items-center gap-2">
                    <span className={`font-['Outfit'] text-[10px] px-2 py-1 rounded ${status.className}`}>
                      {status.label}
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Show More / Show Less Button */}
        {!showAll && restChars.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-8"
          >
            <button
              onClick={() => setShowAll(true)}
              className="group relative px-8 py-3 rounded-lg border border-[#C9A84C]/20 bg-transparent hover:border-[#C9A84C]/40 transition-all duration-500"
              data-testid="show-more-players"
            >
              <span className="font-['Outfit'] text-[#C9A84C]/50 text-xs tracking-[0.3em] group-hover:text-[#C9A84C]/80 transition-colors">
                OPEN {restChars.length} MORE FILES
              </span>
              <div className="absolute inset-0 bg-gradient-to-t from-[#C9A84C]/5 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </motion.div>
        )}
        {showAll && (
          <div className="text-center mt-6">
            <button
              onClick={() => setShowAll(false)}
              className="font-['Outfit'] text-[#E8E0D0]/20 text-[10px] tracking-[0.3em] hover:text-[#E8E0D0]/40 transition-colors"
              data-testid="show-less-players"
            >
              COLLAPSE FILES
            </button>
          </div>
        )}
      </div>

      {/* Character Dossier Modal */}
      <AnimatePresence>
        {selectedCharacter && (() => {
          const clearance = getClearance(selectedCharacter);
          const status = getDisplayStatus(selectedCharacter);
          const dossier = selectedCharacter.dossier;
          const isSealed = clearance === 0;

          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
              onClick={() => setSelectedCharacter(null)}
              data-testid="character-modal-overlay"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="bg-[#0a0c10] border border-[#C9A84C]/20 rounded-lg max-w-2xl w-full relative overflow-hidden my-8"
                onClick={(e) => e.stopPropagation()}
                data-testid="character-modal"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C9A84C] to-transparent opacity-50" />

                <button
                  onClick={() => setSelectedCharacter(null)}
                  className="absolute top-4 right-4 z-10 text-[#E8E0D0]/50 hover:text-[#C9A84C] transition-colors"
                  data-testid="character-modal-close"
                >
                  <X size={24} />
                </button>

                {/* Photo removed per creative direction */}

                {/* Dossier Header */}
                <div className="p-6 sm:p-8 pb-0">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="font-['Outfit'] text-[#8B1A1A] text-xs tracking-widest uppercase font-bold">
                      {isSealed ? 'SEALED' : 'CLASSIFIED'}
                    </span>
                    <span className="font-['Outfit'] text-[#E8E0D0]/30 text-xs">|</span>
                    <span className="font-['Outfit'] text-[#E8E0D0]/40 text-xs tracking-wider">
                      DOSSIER FILE
                    </span>
                    <span className={`font-['Outfit'] text-[10px] px-2 py-0.5 rounded ml-auto ${status.className}`}>
                      {status.label}
                    </span>
                  </div>

                  <h3 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-[#C9A84C] mb-2">
                    {dossier.subject}
                  </h3>

                    {selectedCharacter.archetype && clearance > 0 && selectedCharacter.archetype !== 'CLASSIFIED' && (
                      <p className="font-['Outfit'] text-[#E8E0D0]/50 text-sm italic mb-2">
                        "{selectedCharacter.archetype}"
                      </p>
                    )}
                </div>

                {/* Dossier Fields */}
                <div className="px-6 sm:px-8 pb-6 sm:pb-8">
                  {isSealed ? (
                    /* Fully sealed file */
                    <div className="border-t border-[#8B1A1A]/20 pt-4">
                      <div className="flex items-center gap-3 mb-4">
                        <FileWarning size={18} className="text-[#8B1A1A]/50" />
                        <span className="font-['Outfit'] text-[#8B1A1A]/60 text-xs tracking-widest">THIS FILE IS SEALED PENDING INVESTIGATION</span>
                      </div>
                      <div className="space-y-2 font-['Outfit'] text-sm">
                        {dossier.origin && dossier.origin !== '[CLASSIFIED]' && (
                          <div className="flex flex-wrap">
                            <span className="text-[#E8E0D0]/40 w-28 shrink-0">ORIGIN:</span>
                            <span className="text-[#E8E0D0]/60">{dossier.origin}</span>
                          </div>
                        )}
                        <div className="flex flex-wrap">
                          <span className="text-[#E8E0D0]/40 w-28 shrink-0">ROLE:</span>
                          <span className="text-[#8B1A1A]/50">[REDACTED]</span>
                        </div>
                        <div className="flex flex-wrap">
                          <span className="text-[#E8E0D0]/40 w-28 shrink-0">STATUS:</span>
                          <span className="text-[#8B1A1A]/50">[SEALED]</span>
                        </div>
                        <div className="flex flex-wrap">
                          <span className="text-[#E8E0D0]/40 w-28 shrink-0">INTEL:</span>
                          <span className="text-[#8B1A1A]/50">[PENDING CLEARANCE]</span>
                        </div>
                      </div>
                      {dossier.quote && dossier.quote !== '[CLASSIFIED]' && (
                        <div className="pt-4 mt-4 border-t border-[#C9A84C]/10">
                          <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/40 text-lg italic">
                            "{dossier.quote}"
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Partial / open file — still redact secrets */
                    <div className="space-y-2 font-['Outfit'] text-sm border-t border-[#C9A84C]/10 pt-4 mb-6">
                      {dossier.aliases && dossier.aliases !== '[CLASSIFIED]' && (
                        <div className="flex flex-wrap">
                          <span className="text-[#E8E0D0]/40 w-28 shrink-0">ALIASES:</span>
                          <span className="text-[#E8E0D0]/80">{dossier.aliases}</span>
                        </div>
                      )}
                      <div className="flex flex-wrap">
                        <span className="text-[#E8E0D0]/40 w-28 shrink-0">ORIGIN:</span>
                        <span className="text-[#E8E0D0]/80">{dossier.origin}</span>
                      </div>
                      {dossier.active && (
                        <div className="flex flex-wrap">
                          <span className="text-[#E8E0D0]/40 w-28 shrink-0">ACTIVE:</span>
                          <span className="text-[#E8E0D0]/80">
                            {dossier.active.includes('DECEASED') ? dossier.active.replace(/DECEASED.*/, '[SEALED]') : dossier.active}
                          </span>
                        </div>
                      )}
                      {dossier.method && dossier.method !== '[CLASSIFIED]' && dossier.method !== '[FILE CLASSIFIED]' && (
                        <div className="flex flex-wrap">
                          <span className="text-[#E8E0D0]/40 w-28 shrink-0">METHOD:</span>
                          <span className="text-[#C9A84C]/80 italic">{dossier.method}</span>
                        </div>
                      )}
                      {dossier.strengths && (
                        <div className="flex flex-wrap">
                          <span className="text-[#E8E0D0]/40 w-28 shrink-0">STRENGTHS:</span>
                          <span className="text-[#E8E0D0]/80">{dossier.strengths}</span>
                        </div>
                      )}
                      {/* Flaws — redacted, never show */}
                      <div className="flex flex-wrap">
                        <span className="text-[#E8E0D0]/40 w-28 shrink-0">FLAWS:</span>
                        <span className="text-[#8B1A1A]/50">[REDACTED]</span>
                      </div>
                      {/* Secrets — always hidden */}
                      <div className="flex flex-wrap bg-[#8B1A1A]/5 p-2 rounded mt-2">
                        <span className="text-[#8B1A1A]/60 w-28 shrink-0">CLASSIFIED:</span>
                        <span className="text-[#8B1A1A]/40">This section requires elevated clearance</span>
                      </div>
                      <div className="flex items-center pt-2">
                        <span className="text-[#E8E0D0]/40 w-28 shrink-0">THREAT:</span>
                        <span className="tracking-wider">{renderThreatLevel(dossier.threatLevel)}</span>
                      </div>
                    </div>
                  )}

                  {/* Quote — always show if available and not classified */}
                  {!isSealed && dossier.quote && dossier.quote !== '[CLASSIFIED]' && (
                    <div className="pt-4 border-t border-[#C9A84C]/10">
                      <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/70 text-lg italic">
                        "{dossier.quote}"
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </section>
  );
};

export default CharactersSection;
